import { Trophy, Shield, Eye, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PreviewCardProps {
  onClick?: () => void;
  className?: string;
}

export function JackpotPreviewCard({ onClick, className }: PreviewCardProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-xl p-4 transition-all duration-200",
        "bg-linear-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/30",
        "hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/10",
        "group cursor-pointer",
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-linear-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
          <Trophy className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-amber-500 uppercase tracking-wide">Weekly Jackpot</span>
          </div>
          <p className="text-2xl font-bold text-foreground mt-0.5">$1,000</p>
          <p className="text-xs text-muted-foreground mt-1">Next draw: Monday 12 PM ET</p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-500 transition-colors mt-1" />
      </div>
      <div className="mt-3 pt-3 border-t border-amber-500/20">
        <span className="text-xs font-medium text-amber-500 group-hover:underline">
          Enter Now — It's Free →
        </span>
      </div>
    </button>
  );
}

export function BreachPreviewCard({ onClick, className }: PreviewCardProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-xl p-4 transition-all duration-200",
        "bg-linear-to-br from-red-500/20 to-rose-500/10 border border-red-500/30",
        "hover:border-red-500/50 hover:shadow-lg hover:shadow-red-500/10",
        "group cursor-pointer",
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-linear-to-br from-red-500 to-rose-500 flex items-center justify-center shrink-0">
          <Eye className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-red-500 uppercase tracking-wide">Data Exposure</span>
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          </div>
          <p className="text-sm font-semibold text-foreground mt-0.5">Is your data exposed?</p>
          <p className="text-xs text-muted-foreground mt-1">Check 50+ recent breaches</p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-red-500 transition-colors mt-1" />
      </div>
      <div className="mt-3 pt-3 border-t border-red-500/20">
        <span className="text-xs font-medium text-red-500 group-hover:underline">
          Scan My Email →
        </span>
      </div>
    </button>
  );
}

export function ProtectPreviewCard({ onClick, className }: PreviewCardProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-xl p-4 transition-all duration-200",
        "bg-linear-to-br from-emerald-500/20 to-green-500/10 border border-emerald-500/30",
        "hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/10",
        "group cursor-pointer",
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-linear-to-br from-emerald-500 to-green-500 flex items-center justify-center shrink-0">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-emerald-500 uppercase tracking-wide">Data Brokers</span>
          </div>
          <p className="text-sm font-semibold text-foreground mt-0.5">600+ sites selling your info</p>
          <p className="text-xs text-muted-foreground mt-1">We remove you automatically</p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-emerald-500 transition-colors mt-1" />
      </div>
      <div className="mt-3 pt-3 border-t border-emerald-500/20">
        <span className="text-xs font-medium text-emerald-500 group-hover:underline">
          Remove Me Now →
        </span>
      </div>
    </button>
  );
}

// Export a map for easy lookup
export const RUBY_CARDS = {
  jackpot: JackpotPreviewCard,
  breach: BreachPreviewCard,
  protect: ProtectPreviewCard,
} as const;

export type RubyCardType = keyof typeof RUBY_CARDS;
